import CaseStudies from '@/components/common/case-study-section';
import ContactForm from '@/components/common/contact-form';
import ContactInfo from '@/components/common/contact-info';
import KEYBlue from '@/components/common/keylist-blue';
import ScheduleCall from '@/components/common/schedule-call';
import ClientCarousel from '@/components/home/client-carousel';
import ClientTestimonials from '@/components/home/client-testimonials';
import Image from 'next/image';
import Link from 'next/link';
import React from 'react';

const Page = () => {
    return (
        <div className="relative min-h-screen w-full  flex flex-col">
            {/* Hero Section with Background Video */}
            <div className="relative">
                {/* Background Video - only for hero section */}
                <div className="absolute inset-0 w-full h-full z-0">
                    <video className="w-full h-full object-cover" autoPlay loop muted playsInline>
                        <source src="/media/data-eng-vd.mp4" type="video/mp4" />
                        Your browser does not support the video tag.
                    </video>

                    {/* Dark Overlay - #00000099 (60% opacity black) */}
                    <div className="absolute inset-0 bg-black bg-opacity-60 z-10"></div>
                </div>

                {/* Hero Content */}
                <div className="relative z-20 container mx-auto flex-grow flex flex-col items-start justify-center text-white py-8 md:py-12 lg:py-16 px-4 sm:px-6 lg:px-8">
                    <div className="w-full sm:w-full md:w-[80%] lg:w-[68%]">
                        <div className="inline-flex flex-col lg:items-start gap-[15px] hero-content">
                            <p className="font-semibold text-base sm:text-lg md:text-xl lg:text-2xl text-white mb-0">
                                Scalable Data Solutions by Moweb
                            </p>
                            <h1 className="mt-0 mb-0 text-primary text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-tight md:leading-tight lg:leading-[70px] proximanovacond">
                                Data Engineering Services Company
                            </h1>
                            <p className="mb-0 font-semibold text-base sm:text-base md:text-lg lg:text-xl text-white">
                                Unlock the full potential of your business data with {`Moweb's`} end-to-end Data Engineering
                                services. We design, build, and manage modern data platforms that empower organizations to make
                                data-driven decisions with confidence.
                            </p>
                            <p className="mb-0 font-semibold text-base sm:text-base md:text-lg lg:text-xl text-white">
                                Whether {`you're`} a startup or an enterprise, our scalable data engineering solutions transform
                                raw data into actionable insights—fast, secure, and ready for analysis.
                            </p>

                            <div className="mt-4">
                                <ScheduleCall />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Client Carousel Section - separated from the hero */}
            <div className="w-full">
                <ClientCarousel />
            </div>

            {/* Why Choose Section - with normal background */}
            <div className="py-12 sm:py-16 md:py-20 lg:py-[100px] bg-System_Light_Grey">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="row grid lg:grid-cols-12">
                        <div className="text-center lg:col-span-10 lg:col-start-2 gap-[15px] grid">
                            <h2
                                className="text-primary text-2xl sm:text-2xl lg:text-4xl lg:leading-[50px] font-bold"
                                data-aos="fade-up"
                                data-aos-duration="700"
                                data-aos-delay="100"
                            >
                                Why Choose Moweb as Your Data Engineering Partner?
                            </h2>
                            <p
                                className="text-secondary font-normal text-base sm:text-lg lg:text-xl lg:leading-[30px] mb-0"
                                data-aos="fade-up"
                                data-aos-duration="700"
                                data-aos-delay="200"
                            >
                                Moweb is a leading data engineering company with expertise across ETL pipelines, data lakes, cloud
                                data warehousing, real-time data streaming, and BI tool integration. We help businesses build
                                resilient, high-performance data infrastructure using best-in-class technologies like Snowflake,
                                Databricks, Fivetran, Airflow, and Looker.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
            <div className="py-12 sm:py-16 md:py-20 lg:py-[100px]">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col lg:flex-row gap-6 sm:gap-6 lg:gap-10 items-start lg:items-center pb-6 md:pb-8 lg:pb-10">
                        <div className="w-full lg:w-2/3">
                            <p className="text-primary_one text-lg sm:text-xl sm:leading-[26px] font-bold pb-2 md:pb-[10px] mb-0">
                                End-to-End Data Engineering Services
                            </p>
                            <h3 className="text-2xl sm:text-2xl lg:text-4xl lg:leading-[50px] font-bold pb-2 md:pb-[10px] text-primary_one">
                                Power Your Data Journey from Start to Finish
                            </h3>
                            <p className="text-secondary font-normal text-base sm:text-lg lg:text-xl lg:leading-[30px] mb-0">
                                From ingestion to insights, Moweb helps you build, manage, and scale robust, modern data systems
                                that are secure, cloud-ready, and designed to power real-time analytics and informed
                                decision-making across your organization.
                            </p>
                        </div>
                        <div className="w-full lg:w-1/3 bg-[#FFD10033] border-[#FFD100] border rounded-[10px] p-4 md:p-5">
                            <p className="text-primary_one text-base md:text-[17px] leading-[25px] font-normal mb-[10px]">
                                Schedule a free consultation to explore tailored data engineering solutions that turn your raw
                                data into business-driving intelligence.
                            </p>
                            <div>
                                <Link href="#contact-section" className="btn btn-primary">
                                    {'Schedule a Call'}
                                </Link>
                            </div>
                        </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 lg:gap-10">
                        <div
                            className="card rounded-[10px] p-4 md:p-5 flex flex-col gap-[10px] "
                            data-aos="fade-up"
                            data-aos-duration="700"
                            data-aos-delay="300"
                        >
                            <div className="bg-[#05A6F01A] rounded-[10px] p-2 w-14 h-14 flex items-center justify-center">
                                {' '}
                                <Image
                                    src="/img/icons/pipeline.svg"
                                    width={32}
                                    height={32}
                                    alt="Data Engineering Icon"
                                    loading="lazy"
                                />
                            </div>
                            <h4 className="text-xl md:text-2xl md:leading-[36px] font-bold text-primary_one mb-0">
                                Data Pipeline Development (ETL/ELT)
                            </h4>
                            <p className="text-base md:text-[17px] leading-[25px] text-secondary font-normal mb-0">
                                We design modern ETL and ELT pipelines using tools like Fivetran, Airbyte, and dbt to automate
                                data ingestion, transformation, and loading from multiple sources to a centralized repository.
                            </p>
                            <KEYBlue
                                text="Real-time and batch processing"
                                className="!text-secondary !text-base !md:text-[17px] !leading-[25px] !font-normal !mb-0"
                            />
                            <KEYBlue
                                text="Connect to 100+ data sources (APIs, SaaS, databases)"
                                className="!text-secondary !text-base !md:text-[17px] !leading-[25px] !font-normal !mb-0"
                            />
                            <KEYBlue
                                text="Scalable, secure, and cost-effective"
                                className="!text-secondary !text-base !md:text-[17px] !leading-[25px] !font-normal !mb-0"
                            />
                        </div>
                        <div
                            className="card rounded-[10px] p-4 md:p-5 flex flex-col gap-[10px] "
                            data-aos="fade-up"
                            data-aos-duration="700"
                            data-aos-delay="300"
                        >
                            <div className="bg-[#05A6F01A] rounded-[10px] p-2 w-14 h-14 flex items-center justify-center">
                                {' '}
                                <Image src="/img/icons/cloud.svg" width={32} height={32} alt="cloud data" loading="lazy" />
                            </div>

                            <h4 className="text-xl md:text-2xl md:leading-[36px] font-bold text-primary_one mb-0">
                                Cloud Data Warehousing
                            </h4>
                            <p className="text-base md:text-[17px] leading-[25px] text-secondary font-normal mb-0">
                                Build future-proof data architecture with cloud data warehouse solutions using:
                            </p>
                            <div className="grid grid-cols-2 gap-[10px] items-center ">
                                <div className="flex flex-col gap-[5px] items-center  sm:items-start md:items-center">
                                    <Image src="/img/icons/snowflaks.svg" width={30} height={30} alt="snowflake" />
                                    <p className="text-sm sm:text-base md:text-[17px] leading-[25px] text-secondary mb-0">
                                        Snowflake
                                    </p>
                                </div>
                                <div className="flex flex-col gap-[5px] items-center  sm:items-start md:items-center">
                                    <Image src="/img/icons/amazon.svg" width={30} height={30} alt="Amazon Redshift" />
                                    <p className="text-sm sm:text-base md:text-[17px] leading-[25px] text-secondary mb-0">
                                        Amazon Redshift
                                    </p>
                                </div>
                                <div className="flex flex-col gap-[5px] items-center  sm:items-start md:items-center">
                                    <Image src="/img/icons/bigquery.svg" width={30} height={30} alt="Google BigQuery" />
                                    <p className="text-sm sm:text-base md:text-[17px] leading-[25px] text-secondary mb-0">
                                        Google BigQuery
                                    </p>
                                </div>
                                <div className="flex flex-col gap-[5px] items-center  sm:items-start md:items-center">
                                    <Image
                                        src="/img/icons/azure-synapse-analytics.svg"
                                        width={30}
                                        height={30}
                                        alt="Azure Synapse"
                                    />
                                    <p className="text-sm sm:text-base md:text-[17px] leading-[25px] text-secondary mb-0">
                                        Azure Synapse
                                    </p>
                                </div>
                            </div>
                            <p className="text-base md:text-[17px] leading-[25px] text-secondary font-normal mb-0">
                                We ensure performance, elasticity, and ease of access for all your analytics needs.
                            </p>
                        </div>
                        <div
                            className="card rounded-[10px] p-4 md:p-5 flex flex-col gap-[10px] "
                            data-aos="fade-up"
                            data-aos-duration="700"
                            data-aos-delay="300"
                        >
                            <div className="bg-[#05A6F01A] rounded-[10px] p-2 w-14 h-14 flex items-center justify-center">
                                {' '}
                                <Image src="/img/icons/lakehouse.svg" width={32} height={32} alt="Lakehouse Architecture" />
                            </div>
                            <h4 className="text-xl md:text-2xl md:leading-[36px] font-bold text-primary_one mb-0">
                                Data Lakes & Lakehouse Architecture
                            </h4>
                            <p className="text-base md:text-[17px] leading-[25px] text-secondary font-normal mb-0">
                                Manage large volumes of structured and unstructured data with data lake and lakehouse solutions
                                using:
                            </p>
                            <div className="grid grid-cols-2 gap-[10px] items-center ">
                                <div className="flex flex-col gap-[5px] items-center  sm:items-start md:items-center">
                                    <Image src="/img/icons/databricks_idf4fnhpaJ.svg" width={30} height={30} alt="Databricks" />
                                    <p className="text-sm sm:text-base md:text-[17px] leading-[25px] text-secondary mb-0">
                                        Databricks
                                    </p>
                                </div>
                                <div className="flex flex-col gap-[5px] items-center  sm:items-start md:items-center">
                                    <Image src="/img/icons/xJmjaa-delta-lake.svg" width={30} height={30} alt="Delta Lake" />
                                    <p className="text-sm sm:text-base md:text-[17px] leading-[25px] text-secondary mb-0">
                                        Delta Lake
                                    </p>
                                </div>
                                <div className="flex flex-col gap-[5px] items-center  sm:items-start md:items-center">
                                    <Image src="/img/icons/amazon-s3-logo.svg" width={30} height={30} alt="AWS S3" />
                                    <p className="text-sm sm:text-base md:text-[17px] leading-[25px] text-secondary mb-0">
                                        AWS S3
                                    </p>
                                </div>
                                <div className="flex flex-col gap-[5px] items-center  sm:items-start md:items-center">
                                    <Image
                                        src="/img/icons/data-lake-storage-gen.svg"
                                        width={30}
                                        height={30}
                                        alt="Azure Data Lake"
                                    />
                                    <p className="text-sm sm:text-base md:text-[17px] leading-[25px] text-secondary mb-0">
                                        Azure Data Lake
                                    </p>
                                </div>
                            </div>
                            <p className="text-base md:text-[17px] leading-[25px] text-secondary font-normal mb-0">
                                Ideal for AI/ML pipelines, log storage, and long-term data archiving.
                            </p>
                        </div>
                        <div
                            className="card rounded-[10px] p-4 md:p-5 flex flex-col gap-[10px] "
                            data-aos="fade-up"
                            data-aos-duration="700"
                            data-aos-delay="400"
                        >
                            <div className="bg-[#05A6F01A] rounded-[10px] p-2 w-14 h-14 flex items-center justify-center">
                                {' '}
                                <Image src="/img/icons/business.svg" width={32} height={32} alt="Data Visualization" />
                            </div>
                            <h4 className="text-xl md:text-2xl md:leading-[36px] font-bold text-primary_one mb-0">
                                Business Intelligence & Data Visualization
                            </h4>
                            <p className="text-base md:text-[17px] leading-[25px] text-secondary font-normal mb-0">
                                Turn your data into clear insights with interactive dashboards and advanced reports using tools
                                like:
                            </p>
                            <div className="grid grid-cols-2 gap-[10px] items-center ">
                                <div className="flex flex-col gap-[5px] items-center  sm:items-start md:items-center">
                                    <Image src="/img/icons/looker-icon-svgrepo-com.svg" width={30} height={30} alt="Looker" />
                                    <p className="text-sm sm:text-base md:text-[17px] leading-[25px] text-secondary mb-0">
                                        Looker
                                    </p>
                                </div>
                                <div className="flex flex-col gap-[5px] items-center  sm:items-start md:items-center">
                                    <Image src="/img/icons/pbI-logo.svg" width={30} height={30} alt="Power BI" />
                                    <p className="text-sm sm:text-base md:text-[17px] leading-[25px] text-secondary mb-0">
                                        Power BI
                                    </p>
                                </div>
                                <div className="flex flex-col gap-[5px] items-center  sm:items-start md:items-center">
                                    <Image src="/img/icons/tableau-software.svg" width={30} height={30} alt="Tableau" />
                                    <p className="text-sm sm:text-base md:text-[17px] leading-[25px] text-secondary mb-0">
                                        Tableau
                                    </p>
                                </div>
                                <div className="flex flex-col gap-[5px] items-center  sm:items-start md:items-center">
                                    <Image
                                        src="/img/icons/google-data-studio.svg"
                                        width={30}
                                        height={30}
                                        alt="Google Data Studio"
                                    />
                                    <p className="text-sm sm:text-base md:text-[17px] leading-[25px] text-secondary mb-0">
                                        Google Data Studio
                                    </p>
                                </div>
                            </div>
                            <p className="text-base md:text-[17px] leading-[25px] text-secondary font-normal mb-0">
                                We enable every team in your organization to access the right KPIs and metrics—securely and
                                instantly.
                            </p>
                        </div>
                        <div
                            className="card rounded-[10px] p-4 md:p-5 flex flex-col gap-[10px] "
                            data-aos="fade-up"
                            data-aos-duration="700"
                            data-aos-delay="400"
                        >
                            <div className="bg-[#05A6F01A] rounded-[10px] p-2 w-14 h-14 flex items-center justify-center">
                                {' '}
                                <Image src="/img/icons/real-time.svg" width={32} height={32} alt="Real-Time Data Streaming" />
                            </div>
                            <h4 className="text-xl md:text-2xl md:leading-[36px] font-bold text-primary_one mb-0">
                                Real-Time Data Streaming
                            </h4>
                            <p className="text-base md:text-[17px] leading-[25px] text-secondary font-normal mb-0">
                                Build real-time applications and monitor key metrics live with streaming data platforms like:
                            </p>
                            <div className="grid grid-cols-2 gap-[10px] items-center ">
                                <div className="flex flex-col gap-[5px] items-center  sm:items-start md:items-center">
                                    <Image src="/img/icons/apache_kafka_logo.svg" width={30} height={30} alt="Apache Kafka" />
                                    <p className="text-sm sm:text-base md:text-[17px] leading-[25px] text-secondary mb-0">
                                        Apache Kafka
                                    </p>
                                </div>
                                <div className="flex flex-col gap-[5px] items-center  sm:items-start md:items-center">
                                    <Image src="/img/icons/flink-icon-seeklogo.svg" width={30} height={30} alt="Apache Flink" />
                                    <p className="text-sm sm:text-base md:text-[17px] leading-[25px] text-secondary mb-0">
                                        Apache Flink
                                    </p>
                                </div>
                                <div className="flex flex-col gap-[5px] items-center  sm:items-start md:items-center">
                                    <Image src="/img/icons/amazon_kinesis-icon.svg" width={30} height={30} alt="AWS Kinesis" />
                                    <p className="text-sm sm:text-base md:text-[17px] leading-[25px] text-secondary mb-0">
                                        AWS Kinesis
                                    </p>
                                </div>
                                <div className="flex flex-col gap-[5px] items-center  sm:items-start md:items-center">
                                    <Image
                                        src="/img/icons/apache_spark-icon.svg"
                                        width={30}
                                        height={30}
                                        alt="Spark Structured Streaming"
                                    />
                                    <p className="text-sm sm:text-base md:text-[17px] leading-[25px] text-secondary mb-0 sm:text-left md:text-center">
                                        Spark Structured Streaming
                                    </p>
                                </div>
                            </div>
                            <p className="text-base md:text-[17px] leading-[25px] text-secondary font-normal mb-0">
                                Perfect for fraud detection, recommendation engines, and IoT applications.
                            </p>
                        </div>
                        <div
                            className="card rounded-[10px] p-4 md:p-5 flex flex-col gap-[10px] "
                            data-aos="fade-up"
                            data-aos-duration="700"
                            data-aos-delay="400"
                        >
                            <div className="bg-[#05A6F01A] rounded-[10px] p-2 w-14 h-14 flex items-center justify-center">
                                {' '}
                                <Image
                                    src="/img/icons/modeling.svg"
                                    width={32}
                                    height={32}
                                    alt="Data Modeling, Governance & Quality"
                                />
                            </div>
                            <h4 className="text-xl md:text-2xl md:leading-[36px] font-bold text-primary_one mb-0">
                                Data Modeling, Governance & Quality
                            </h4>
                            <p className="text-base md:text-[17px] leading-[25px] text-secondary font-normal mb-0">
                                We structure your data and ensure trust with:
                            </p>
                            <KEYBlue
                                text="Dimensional modeling and schema design"
                                className="!text-secondary !text-base !md:text-[17px] !leading-[25px] !font-normal !mb-0"
                            />
                            <KEYBlue
                                text="Data validation and quality checks"
                                className="!text-secondary !text-base !md:text-[17px] !leading-[25px] !font-normal !mb-0"
                            />
                            <KEYBlue
                                text="Data cataloging and lineage using Great Expectations, Monte Carlo, and Collibra"
                                className="!text-secondary !text-base !md:text-[17px] !leading-[25px] !font-normal !mb-0"
                            />
                        </div>
                    </div>
                </div>
            </div>
            <div className="py-12 sm:py-16 md:py-20 lg:py-[100px] bg-System_Light_Grey">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="row grid md:grid-cols-12 justify-center">
                        <div className=" md:col-span-4">
                            <div className="text-start sticky sticky-list">
                                <h3 className="text-primary_one text-3xl sm:text-3xl lg:text-5xl lg:leading-[64px] font-bold mb-[15px]">
                                    Industries We Serve with Data Engineering Services
                                </h3>
                                <p className="text-base sm:text-lg lg:text-xl lg:leading-[30px] text-secondary font-normal mb-[15px]">
                                    From startups to global enterprises, we empower every industry to unlock smarter decisions
                                    with scalable, real-time data solutions.
                                </p>

                                <div>
                                    <Link href="#contact-section" className="btn btn-primary">
                                        {'Talk to a Data Expert'}
                                    </Link>
                                </div>
                            </div>
                        </div>
                        <div className="md:col-span-8  ">
                            <div className="grid grid-cols-2 sm:grid-cols-1 md:grid-cols-2 sticky-content gap-10">
                                <div>
                                    <div className="flex flex-col gap-[15px] pb-6 md:pb-10">
                                        <Image
                                            src="/img/retailecommerce.png"
                                            width={400}
                                            height={250}
                                            alt="Retail & eCommerce"
                                            className="rounded-[10px] w-full h-auto"
                                        />
                                        <div>
                                            <p className="text-primary_one text-lg md:text-xl md:leading-[26px] font-bold mb-[5px]">
                                                Retail & eCommerce
                                            </p>
                                            <p className="text-secondary text-base md:text-[17px] leading-[21px] mb-0 font-normal">
                                                Customer analytics, sales forecasting, inventory insights
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex flex-col gap-[15px] pb-6 md:pb-10">
                                        <Image
                                            src="/img/eddtechh.png"
                                            width={400}
                                            height={250}
                                            alt="EdTech"
                                            className="rounded-[10px] w-full h-auto"
                                        />
                                        <div>
                                            <p className="text-primary_one text-lg md:text-xl  md:leading-[26px] font-bold mb-[5px]">
                                                EdTech
                                            </p>
                                            <p className="text-secondary text-base md:text-[17px] leading-[21px] mb-0 font-normal">
                                                Student progress dashboards, engagement metrics, learning analytics
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex flex-col gap-[15px]">
                                        <Image
                                            src="/img/fintech.png"
                                            width={400}
                                            height={250}
                                            alt="FinTech"
                                            className="rounded-[10px] w-full h-auto"
                                        />
                                        <div>
                                            <p className="text-primary_one text-lg md:text-xl  md:leading-[26px] font-bold mb-[5px]">
                                                FinTech
                                            </p>
                                            <p className="text-secondary text-base md:text-[17px] leading-[21px] mb-0 font-normal">
                                                Real-time fraud detection, risk modeling, regulatory reporting
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                <div>
                                    <div className="flex flex-col gap-[15px] pb-6 md:pb-10">
                                        <Image
                                            src="/img/healthcare.png"
                                            width={400}
                                            height={250}
                                            alt="Healthcare"
                                            className="rounded-[10px] w-full h-auto"
                                        />
                                        <div>
                                            <p className="text-primary_one text-lg md:text-xl font-bold  md:leading-[26px] mb-[5px]">
                                                Healthcare
                                            </p>
                                            <p className="text-secondary text-base md:text-[17px] leading-[21px] mb-0 font-normal">
                                                Patient record integration, HIPAA-compliant data pipelines
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex flex-col gap-[15px] pb-6 md:pb-10">
                                        <Image
                                            src="/img/marketing.png"
                                            width={400}
                                            height={250}
                                            alt="Marketing & Advertising"
                                            className="rounded-[10px] w-full h-auto"
                                        />
                                        <div>
                                            <p className="text-primary_one text-lg md:text-xl  md:leading-[26px] font-bold mb-[5px]">
                                                Marketing & Advertising
                                            </p>
                                            <p className="text-secondary text-base md:text-[17px] leading-[21px] mb-0 font-normal">
                                                Campaign performance tracking, customer segmentation, ROI analytics
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex flex-col gap-[15px]">
                                        <Image
                                            src="/img/logisticssupplychain.png"
                                            width={400}
                                            height={250}
                                            alt="Logistics & Supply Chain"
                                            className="rounded-[10px] w-full h-auto"
                                        />
                                        <div>
                                            <p className="text-primary_one text-lg md:text-xl  md:leading-[26px] font-bold mb-[5px]">
                                                Logistics & Supply Chain
                                            </p>
                                            <p className="text-secondary text-base md:text-[17px] leading-[21px] mb-0 font-normal">
                                                IoT data ingestion, fleet optimization, demand prediction
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="py-12 sm:py-16 md:py-20 lg:py-[100px] bg-primary">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <div>
                        <h3 className="text-white text-3xl sm:text-3xl lg:text-5xl lg:leading-[64px] mb-0 pb-[15px] font-bold">
                            Benefits of Partnering with Moweb
                        </h3>
                        <p className="text-white font-normal mb-0 text-base sm:text-lg lg:text-xl lg:leading-[30px] pb-6 md:pb-8 lg:pb-10">
                            Discover the key advantages that make our data engineering solutions scalable, secure, and
                            future-ready.
                        </p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6 md:gap-8 lg:gap-10 items-start">
                            <div>
                                <div className="bg-[white] rounded-[10px] p-2 w-14 h-14 flex items-center justify-center mb-4">
                                    <Image src="/img/icons/cloud-native.svg" width={30} height={30} alt="clode-native" />
                                </div>
                                <p className="text-lg md:text-xl md:leading-[26px] text-white font-bold mb-0">
                                    Cloud-native & platform-agnostic architecture
                                </p>
                            </div>
                            <div>
                                <div className="bg-[white] rounded-[10px] p-2 w-14 h-14 flex items-center justify-center mb-4">
                                    <Image src="/img/icons/enterprise-grade.svg" width={30} height={30} alt="Enterprise-grade" />
                                </div>
                                <p className="text-lg md:text-xl md:leading-[26px] text-white font-bold mb-0">
                                    Enterprise-grade data security and compliance
                                </p>
                            </div>
                            <div>
                                <div className="bg-[white] rounded-[10px] p-2 w-14 h-14 flex items-center justify-center mb-4">
                                    <Image src="/img/icons/fully-manage.svg" width={30} height={30} alt="Fully managed" />
                                </div>
                                <p className="text-lg md:text-xl md:leading-[26px] text-white font-bold mb-0">
                                    Fully managed and monitored data pipelines
                                </p>
                            </div>
                            <div>
                                <div className="bg-[white] rounded-[10px] p-2 w-14 h-14 flex items-center justify-center mb-4">
                                    <Image src="/img/icons/expertise-top.svg" width={30} height={30} alt="clode-native" />
                                </div>
                                <p className="text-lg md:text-xl md:leading-[26px] text-white font-bold mb-0">
                                    Expertise in top data engineering platforms
                                </p>
                            </div>
                            <div>
                                <div className="bg-[white] rounded-[10px] p-2 w-14 h-14 flex items-center justify-center mb-4">
                                    <Image
                                        src="/img/icons/scalable-architecture.svg"
                                        width={30}
                                        height={30}
                                        alt="Scalable architecture "
                                    />
                                </div>
                                <p className="text-lg md:text-xl md:leading-[26px] text-white font-bold mb-0">
                                    Scalable architecture that grows with your business
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <CaseStudies />
            <ClientTestimonials />
            <div id="contact-section" className="section bg-primary py-100 dark:text-light dark:bg-black">
                <div className="container lg:container md:mx-auto">
                    <div className="row grid md:grid-cols-12 lg:grid-cols-12">
                        <ContactForm />
                        <ContactInfo />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Page;
